/**
 * Raw Material Delivery Service API Client
 * Handles all API calls for raw material delivery management
 *
 * @author SAUBHAGYA Development Team
 * @since Raw Material Deliveries Module
 *
 * @description
 * This service provides access to raw material delivery data using
 * the existing manufacturing service routes endpoint.
 *
 * Note: The backend doesn't have dedicated /raw-material-deliveries endpoints.
 * All data comes from the /manufacturing/{id}/routes/scheduled endpoint.
 */

import { transportApi, RawMaterialRoute } from '../transport/api';
import { API_ENDPOINTS } from '../../lib/apiConfig';

// ============================================
// TYPE DEFINITIONS
// ============================================

/**
 * Incoming Delivery status workflow: UPCOMING → IN_TRANSIT → RECEIVED (or CANCELLED)
 */
export type IncomingDeliveryStatus = 'UPCOMING' | 'IN_TRANSIT' | 'RECEIVED' | 'CANCELLED';

/**
 * Incoming Delivery DTO - from the new mfg_incoming_delivery table
 * Tracks deliveries from transport routes with real-time status updates
 */
export interface IncomingDeliveryDTO {
  id: number;
  manufacturingUnitId: number;
  routeExecutionId: number;
  routeId?: number;
  routeCode?: string;
  routeName?: string;
  executionDate: string;
  driverId?: number;
  driverName?: string;
  driverPhone?: string;
  vehicleNumber?: string;
  status: IncomingDeliveryStatus;
  routeStartedAt?: string;
  routeCompletedAt?: string;
  receivedAt?: string;
  totalStops?: number;
  stopsCompleted?: number;
  stopsSkipped?: number;
  expectedQuantityKg?: number;
  actualQuantityKg?: number;
  actualDistanceKm?: number;
  actualDurationMin?: number;
  notes?: string;
  createdAt: string;
  updatedAt?: string;
}

/**
 * Statistics for incoming deliveries
 */
export interface IncomingDeliveryStats {
  total: number;
  upcoming: number;
  inTransit: number;
  received: number;
  cancelled: number;
  totalExpectedQuantityKg?: number;
  totalReceivedQuantityKg?: number;
}

export interface RawMaterialDeliveryDTO {
  id: number;
  routeId: number;
  routeCode: string;
  routeName: string;
  status: 'DRAFT' | 'ACTIVE' | 'PAUSED' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED';
  statusName?: string;
  deliveryDate?: string;
  materialType?: string;
  materialTypeId?: number;
  expectedQuantityKg?: number;
  actualQuantityKg?: number;
  driverId?: number;
  driverName?: string;
  driverPhone?: string;
  vehicleId?: number;
  vehicleNumber?: string;
  vehicleType?: string;
  totalStops?: number;
  completedStops?: number;
  skippedStops?: number;
  manufacturingUnitId?: number;
  manufacturingUnitName?: string;
  totalDistanceKm?: number;
  estimatedDurationMin?: number;
  actualDurationMin?: number;
  startedAt?: string;
  completedAt?: string;
  createdAt: string;
  updatedAt?: string;
  notes?: string;

  // ===== Execution Progress Fields (for real-time tracking) =====
  /** Today's execution ID (null if no execution exists for today) */
  todayExecutionId?: number;
  /** Today's execution status: SCHEDULED, STARTED, IN_PROGRESS, COMPLETED */
  todayExecutionStatus?: string;
  /** Current stop index the driver is at (1-based) */
  currentStopIndex?: number;
  /** Name of the gaushala the driver is currently at */
  currentStopGaushalaName?: string;
  /** Status at current stop: PENDING, ARRIVED, COMPLETED, SKIPPED */
  currentStopStatus?: string;
  /** Number of stops completed so far today */
  completedStopsToday?: number;
  /** Number of stops skipped today */
  skippedStopsToday?: number;
  /** Total quantity collected today */
  totalQuantityCollectedToday?: number;
  /** When today's execution started */
  executionStartedAt?: string;
  /** Human-readable progress message: "At Stop 2 of 5" */
  progressMessage?: string;
}

// ============================================
// CONFIGURATION
// ============================================

/**
 * Get JWT token from localStorage
 */
function getAuthToken(): string | null {
  return localStorage.getItem('saubhagya_jwt_token');
}

/**
 * Get authorization headers
 */
function getHeaders(): HeadersInit {
  const token = getAuthToken();
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` })
  };
}

// ============================================
// ERROR HANDLING
// ============================================

/**
 * Custom error class for API errors
 */
export class DeliveryApiError extends Error {
  constructor(
    message: string,
    public statusCode?: number,
    public errors?: string[]
  ) {
    super(message);
    this.name = 'DeliveryApiError';
  }
}

// ============================================
// API METHODS
// ============================================

/**
 * Raw Material Delivery API client
 */
export const rawMaterialDeliveryApi = {
  /**
   * Get scheduled/active routes for the current manufacturing unit
   * Uses the existing working API endpoint via manufacturing service
   * For Admin users without a manufacturing profile, falls back to listing all routes
   * @returns Promise with list of scheduled routes as delivery DTOs
   */
  getScheduledDeliveries: async (): Promise<RawMaterialDeliveryDTO[]> => {
    try {
      let routes: RawMaterialRoute[] = [];
      let manufacturingUnitId: number | null = null;

      // Try to get manufacturing unit ID from user profile
      try {
        const profileResponse = await fetch(
          API_ENDPOINTS.manufacturing.getProfile(),
          {
            method: 'GET',
            headers: getHeaders()
          }
        );

        if (profileResponse.ok) {
          const profileData = await profileResponse.json();
          manufacturingUnitId = profileData.data?.id || profileData.id;
        }
      } catch (profileError) {
        console.warn('Profile fetch failed, will try listing all routes:', profileError);
      }

      if (manufacturingUnitId) {
        // Use manufacturing unit specific endpoint
        routes = await transportApi.getScheduledRoutes(manufacturingUnitId);
      } else {
        // Fallback for Admin users: get all routes using transport API
        console.log('No manufacturing unit profile, fetching all routes for Admin view');
        const result = await transportApi.listRoutes(0, 100);
        routes = result.content || [];
      }

      // Get today's executions to enrich delivery data
      let todayExecutions: Map<number, any> = new Map();
      try {
        const executions = await transportApi.getExecutions(true);
        const today = new Date().toISOString().split('T')[0];
        executions.forEach(exec => {
          if (exec.executionDate?.split('T')[0] === today) {
            todayExecutions.set(exec.routeId, exec);
          }
        });
      } catch (execError) {
        console.warn('Failed to fetch executions for enrichment:', execError);
      }

      // Transform routes to delivery DTOs, preferring today's execution
      // for any field that has both a "planned" and an "actual" version.
      const deliveries: RawMaterialDeliveryDTO[] = routes.map((route: RawMaterialRoute) => {
        // Get today's execution for this route (if scheduler created one)
        const todayExec = todayExecutions.get(route.id || 0);

        // Date resolution priority: today's execution date > route's scheduleConfig.date > route createdAt.
        // Without this, every row showed the same scheduleConfig.date value because most routes
        // share the same template config.
        let deliveryDate: string | undefined = todayExec?.executionDate
          ? String(todayExec.executionDate).split('T')[0]
          : undefined;
        if (!deliveryDate && route.scheduleConfig) {
          try {
            const config = typeof route.scheduleConfig === 'string'
              ? JSON.parse(route.scheduleConfig)
              : route.scheduleConfig;
            deliveryDate = config.date;
          } catch (e) {
            console.warn('Failed to parse scheduleConfig:', e);
          }
        }

        // Distance/duration/quantity: when there's a real execution, use the actuals
        // (otherwise the modal shows the route's planned 0/0/0 placeholders).
        const hasExecData = todayExec != null;
        const totalDistanceKm = hasExecData && Number(todayExec.actualDistanceKm) > 0
          ? Number(todayExec.actualDistanceKm)
          : route.totalDistanceKm;
        const actualDurationMin = hasExecData && Number(todayExec.actualDurationMin) > 0
          ? Number(todayExec.actualDurationMin)
          : undefined;
        const actualQuantityKg = hasExecData
          ? Number(todayExec.totalQuantityCollectedKg ?? 0)
          : undefined;

        return {
          id: route.id || 0,
          routeId: route.id || 0,
          routeCode: route.routeCode,
          routeName: route.routeName,
          status: (route.status || route.statusCode || 'ACTIVE') as RawMaterialDeliveryDTO['status'],
          statusName: route.statusName,
          deliveryDate,
          driverId: route.assignedDriverId,
          driverName: route.assignedDriverName,
          vehicleId: route.assignedVehicleId,
          vehicleNumber: route.assignedVehicleNumber,
          totalStops: route.totalStops ?? (route.stops?.length || undefined),
          totalDistanceKm,
          estimatedDurationMin: route.estimatedDurationMin,
          actualDurationMin,
          actualQuantityKg,
          manufacturingUnitId: route.manufacturingUnitId,
          manufacturingUnitName: route.mfgUnitName,
          createdAt: route.createdAt || new Date().toISOString(),
          startedAt: route.routeStartTime,
          completedAt: todayExec?.completedAt,
          // Execution progress fields
          todayExecutionId: todayExec?.id,
          todayExecutionStatus: todayExec?.statusCode || todayExec?.status,
          completedStopsToday: todayExec?.totalStopsCompleted,
          skippedStopsToday: todayExec?.totalStopsSkipped,
          totalQuantityCollectedToday: todayExec?.totalQuantityCollectedKg,
          executionStartedAt: todayExec?.startedAt,
        };
      });

      return deliveries;
    } catch (error) {
      console.error('Failed to fetch scheduled deliveries:', error);
      if (error instanceof DeliveryApiError) throw error;
      throw new DeliveryApiError(
        error instanceof Error ? error.message : 'Failed to fetch scheduled deliveries'
      );
    }
  },

  /**
   * Get active incoming deliveries (UPCOMING or IN_TRANSIT)
   * Uses the new mfg_incoming_delivery table
   * @returns Promise with list of active incoming deliveries
   */
  getActiveIncomingDeliveries: async (): Promise<IncomingDeliveryDTO[]> => {
    try {
      // Get manufacturing unit ID from profile
      let manufacturingUnitId: number | null = null;
      try {
        const profileResponse = await fetch(
          API_ENDPOINTS.manufacturing.getProfile(),
          {
            method: 'GET',
            headers: getHeaders()
          }
        );

        if (profileResponse.ok) {
          const profileData = await profileResponse.json();
          manufacturingUnitId = profileData.data?.id || profileData.id;
        }
      } catch (profileError) {
        console.warn('Profile fetch failed:', profileError);
        return [];
      }

      if (!manufacturingUnitId) {
        console.warn('No manufacturing unit ID available');
        return [];
      }

      const response = await fetch(
        `${API_ENDPOINTS.manufacturing.base}/manufacturing/${manufacturingUnitId}/deliveries/active`,
        {
          method: 'GET',
          headers: getHeaders()
        }
      );

      if (!response.ok) {
        if (response.status === 404) {
          // Endpoint not yet deployed - return empty array
          console.warn('Active incoming deliveries endpoint not available yet');
          return [];
        }
        throw new DeliveryApiError('Failed to fetch active incoming deliveries', response.status);
      }

      const data = await response.json();
      return data.data || data || [];
    } catch (error) {
      console.error('Failed to fetch active incoming deliveries:', error);
      if (error instanceof DeliveryApiError) throw error;
      // Return empty array if endpoint doesn't exist yet
      return [];
    }
  },

  /**
   * Get today's incoming deliveries.
   *
   * Path resolution:
   * 1. Try the user's manufacturing-unit profile and call the per-unit endpoint
   *    /manufacturing/{id}/deliveries/today.
   * 2. Profile unavailable (admin or unassigned) → fall back to the cross-unit
   *    aggregate /deliveries/today/all (admin-only, returns every unit's rows).
   * 3. If neither resolves data, return [] so the caller renders the empty state.
   */
  getTodayIncomingDeliveries: async (): Promise<IncomingDeliveryDTO[]> => {
    let manufacturingUnitId: number | null = null;
    try {
      const profileResponse = await fetch(
        API_ENDPOINTS.manufacturing.getProfile(),
        {
          method: 'GET',
          headers: getHeaders()
        }
      );
      if (profileResponse.ok) {
        const profileData = await profileResponse.json();
        manufacturingUnitId = profileData.data?.id || profileData.id;
      }
    } catch (profileError) {
      console.warn('Profile fetch failed, will try admin aggregate:', profileError);
    }

    const url = manufacturingUnitId
      ? `${API_ENDPOINTS.manufacturing.base}/manufacturing/${manufacturingUnitId}/deliveries/today`
      : `${API_ENDPOINTS.manufacturing.base}/deliveries/today/all`;

    try {
      const response = await fetch(url, { method: 'GET', headers: getHeaders() });

      if (!response.ok) {
        if (response.status === 404 || response.status === 403) {
          // Endpoint not deployed yet, or current user not authorised for the
          // admin aggregate. Either way, surface an empty list rather than throwing.
          console.warn(`Today incoming deliveries unavailable (status=${response.status}, url=${url})`);
          return [];
        }
        throw new DeliveryApiError('Failed to fetch today incoming deliveries', response.status);
      }

      const data = await response.json();
      return data.data || data || [];
    } catch (error) {
      console.error('Failed to fetch today incoming deliveries:', error);
      if (error instanceof DeliveryApiError) throw error;
      return [];
    }
  },

  /**
   * Get incoming delivery statistics
   * @returns Promise with delivery statistics
   */
  getIncomingDeliveryStats: async (): Promise<IncomingDeliveryStats> => {
    try {
      // Get manufacturing unit ID from profile
      let manufacturingUnitId: number | null = null;
      try {
        const profileResponse = await fetch(
          API_ENDPOINTS.manufacturing.getProfile(),
          {
            method: 'GET',
            headers: getHeaders()
          }
        );

        if (profileResponse.ok) {
          const profileData = await profileResponse.json();
          manufacturingUnitId = profileData.data?.id || profileData.id;
        }
      } catch (profileError) {
        console.warn('Profile fetch failed:', profileError);
        return { total: 0, upcoming: 0, inTransit: 0, received: 0, cancelled: 0 };
      }

      if (!manufacturingUnitId) {
        console.warn('No manufacturing unit ID available');
        return { total: 0, upcoming: 0, inTransit: 0, received: 0, cancelled: 0 };
      }

      const response = await fetch(
        `${API_ENDPOINTS.manufacturing.base}/manufacturing/${manufacturingUnitId}/deliveries/statistics`,
        {
          method: 'GET',
          headers: getHeaders()
        }
      );

      if (!response.ok) {
        if (response.status === 404) {
          console.warn('Delivery statistics endpoint not available yet');
          return { total: 0, upcoming: 0, inTransit: 0, received: 0, cancelled: 0 };
        }
        throw new DeliveryApiError('Failed to fetch delivery statistics', response.status);
      }

      const data = await response.json();
      return data.data || data;
    } catch (error) {
      console.error('Failed to fetch delivery statistics:', error);
      if (error instanceof DeliveryApiError) throw error;
      return { total: 0, upcoming: 0, inTransit: 0, received: 0, cancelled: 0 };
    }
  }
};

export default rawMaterialDeliveryApi;
